import { ActivatedRoute, RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { Observable, combineLatest, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
} from 'rxjs/operators';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  FormControl,
  FormGroup,
  Validators,
  FormGroupDirective,
  NgForm,
  FormArray,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  TimeFrame,
  CropsGradesCondition,
  Tolerance,
  DeliveryType,
  DeliveryOption,
  ContainerType,
  PaymentTerm,
  CropYear,
  RulesToGovern,
  WeightGradeToGovern,
  Origin,
  Specifications,
  Timeframe,
  Commodity,
  Units,
  PriceUnit,
  PriceType,
  MarketType,
  futuresMonths,
  futuresYears,
  Documentation,
  SpecificationType,
} from 'src/app/components/bids-and-offers/lots.model';
import { NewLotOptionsService } from 'src/app/services/new-lot-options.service';
import { CreateLotService } from 'src/app/services/create-lot.service';
import { UserService } from 'src/app/services/user.service';
import { UserDetails } from '../../login/user.model';
import { Store } from '@ngrx/store';
import { UserState } from 'src/app/store/user/user.reducer';
import { TableDataState } from 'src/app/store/table-data/table-data.reducer';
import { DestinationSearchService } from 'src/app/services/destination-search.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { NewLotSummaryDialogComponent } from './new-lot-summary-dialog/new-lot-summary-dialog.component';
import { LotsService } from 'src/app/services/lots.service';
import { locationValidator } from '../../custom/form-validators/location.validator';
import { priceValidator } from '../../custom/form-validators/price.validator';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { LogEventsEnum } from 'src/app/firebase.model';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { BlockUserRolesDirective } from 'src/app/directives/block-user-roles.directive';
import { DomesticPipe } from 'src/app/pipes/domestic-city.pipe';
import { PriceTypePipe } from 'src/app/pipes/price-type.pipe';
import { SameYearPipe } from 'src/app/pipes/same-year.pipe';
import { LotDetailsCardComponent } from '../desktop-view/lot-details-card/lot-details-card.component';
import { NegotiationDetailsCardComponent } from '../desktop-view/negotiation-details-card/negotiation-details-card.component';
import { TradesViewerComponent } from '../desktop-view/trades-viewer/trades-viewer.component';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AutocompleteComponent } from '../../custom/autocomplete/autocomplete.component';
import { selectTableData } from 'src/app/store/table-data/table-data.selectors';
import { selectUser } from 'src/app/store/user/user.selectors';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    const isSubmitted = form && form.submitted;
    const formGroup = control?.parent; // Get the parent form group

    // Check if control is invalid or if the form group has the 'priceRequired' error
    const hasFormGroupError = formGroup && formGroup.hasError('priceRequired');

    return !!(
      (control &&
        control.invalid &&
        (isSubmitted || control.dirty || control.touched)) ||
      (hasFormGroupError && (control?.touched || control?.dirty))
    );
  }
}

@Component({
  selector: 'app-new-lot',
  templateUrl: './new-lot.component.html',
  styleUrls: ['./new-lot.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatCardModule,
    MatChipsModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatTableModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatSortModule,
    MatPaginatorModule,
    FontAwesomeModule,
    MatIconModule,
    AutocompleteComponent,
  ],
})
export class NewLotComponent implements OnInit, OnDestroy {
  private analytics = inject(Analytics);
  controlTypes = SpecificationType;
  basisMonths: { label: string; value: string }[];
  basisYears: number[];
  basisQuote = signal<string>(null);

  currentDocumentations = signal<Documentation[]>(null);

  searchingLocation = signal(false);
  userDetails$: Observable<UserDetails>;
  repostingLot = signal(false);
  defaultCommodity = signal<string>(null);
  favouriteCommodities = signal<string[]>([]);
  recentlySearchedLocations = signal<string[]>([]);
  userId: string;
  // marketSub: Subscription; Not Used Anymore
  marketType = signal<MarketType>(null);

  lotType: string;
  buttonOpacity = 1;
  lastScrollTop = 0;
  origins$: Observable<Origin[]>;
  cropsGradesConditions$: Observable<CropsGradesCondition[]>;
  commodities: Commodity[] = [];
  productTypes$: Observable<{ name: string }[]>; // Observable for product types
  showProductTypeControl: boolean = false;
  specifications$: Observable<Specifications[]>;
  documentations$: Observable<Documentation[]>;
  grades$: Observable<{ name: string }[]>; // Observable for grades
  showGradeControl: boolean = false;
  priceTypes$: Observable<PriceType[]>;
  timeFrames$: Observable<TimeFrame[]>;
  cropYears$: Observable<CropYear[]>;
  gradeDeliveryTypes$: Observable<{ name: string }[]>;
  deliveryTypeOptions$: Observable<{ name: string }[]>;
  deliveryTypes$: Observable<DeliveryType[]>;
  rulesToGovern$: Observable<RulesToGovern[]>;
  weightGradeToGovern$: Observable<WeightGradeToGovern[]>;
  priceUnits$: Observable<PriceUnit[]>;
  units$: Observable<Units[]>;
  destinations$: Observable<{ name: string }[]>;
  packagingTypes$: Observable<{ name: string }[]>;
  paymentTerms$: Observable<PaymentTerm[]>;
  tolerances$: Observable<Tolerance[]>;
  deliveryOptions$: Observable<DeliveryOption[]>;
  containerTypes$: Observable<ContainerType[]>;
  // destinationTypes$: Observable<DestinationType[]> = of([
  //   { name: 'Domestic City' },
  //   { name: 'Foreign Port' },
  // ]);
  // currencies: Currency[] = [{ name: 'CAD' }, { name: 'USD' }];
  currencies$: Observable<{ name: string }[]>;
  currencyPrefix: string;
  matcher = new MyErrorStateMatcher();
  minDate: Date;
  isLoading: boolean;
  newLotForm: FormGroup;

  constructor(
    private newLotOptionsService: NewLotOptionsService,
    private createLotService: CreateLotService,
    private searchDestinationsService: DestinationSearchService,
    private lotsService: LotsService,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private _snackBar: MatSnackBar,
    private store: Store<{
      user: UserState;
      market: string;
      lotTableData: TableDataState;
    }>,
    private dialog: MatDialog
  ) {
    this.basisMonths = futuresMonths;
    this.basisYears = futuresYears();
    // used for favourite crops in template
    // this.userDetails$ = this.store.select('user').pipe(
    //   map((res) => {
    //     if (res.user) {
    //       this.userId = res.user._id;
    //     } else {
    //       this.router.navigate(['../']);
    //       return;
    //     }
    //     if (res.user.default_origin) {
    //       this.newLotForm
    //         .get('originControl')
    //         .patchValue(res.user.default_origin, { emitValue: false });
    //     }
    //     if (res.user.default_commodity) {
    //       this.newLotForm
    //         .get('cropTypeControl')
    //         .setValue(res.user.default_commodity);
    //     }
    //     console.log('userDetails', res);
    //     return res.user;
    //   })
    // );
    // this.marketSub = this.store.select('market').subscribe((res) => {
    //   console.log('MARKET TYPE STATE V0', res);
    //   // this.marketType = res['market'];
    // });
  }

  ngOnInit() {
    this.store
      .select(selectTableData)
      .pipe(
        map((res) => {
          if (res) {
            this.marketType.set(res.selectedMarket);
          } else {
            this.router.navigate['../'];
          }
        })
      )
      .subscribe();

    // Define lotType based on the last segment of the URL
    this.lotType =
      this.route.snapshot.url[this.route.snapshot.url.length - 1].path;
    this.lotType = this.lotType.charAt(0).toUpperCase() + this.lotType.slice(1);
    logEvent(this.analytics, LogEventsEnum.lots_create_view, {
      lot_type: this.lotType,
    });
    // Define Form Group and its Form Controls
    this.newLotForm = new FormGroup(
      {
        originControl: new FormControl(null),
        cropTypeControl: new FormControl(
          this.userService.getUserDetails().default_commodity // TODO: fix this
        ),
        productTypeControl: new FormControl(null),
        specificationsControl: new FormArray([]),
        documentationsControl: new FormArray([]),
        gradeControl: new FormControl(null),
        timeFrameControl: new FormControl(null, Validators.required),
        indicationControl: new FormControl(false),
        cropYearControl: new FormControl(null),
        quantityControl: new FormControl('', [
          Validators.required,
          Validators.pattern(/^[1-9]\d*$/),
        ]),
        toleranceControl: new FormControl(null),
        currencyControl: new FormControl(null),
        priceTypeControl: new FormControl(null),
        priceControl: new FormControl(0, [
          Validators.pattern(/^\d+(\.\d{1,4})?$/),
        ]),
        basisPriceControl: new FormGroup({
          basisPoints: new FormControl(0, [
            Validators.pattern(/^-?\d+(\.\d{0,4})?$/),
          ]),
          month: new FormControl(null),
          product_code: new FormControl(''),
          year: new FormControl(0),
        }),
        priceUnitsControl: new FormControl('', [Validators.required]),
        deliveryWindowControl: new FormGroup({
          start: new FormControl(null, Validators.required),
          end: new FormControl(null, Validators.required),
        }),
        deliveryTypeControl: new FormControl(null),
        deliveryOptionControl: new FormControl(null),
        containerTypeControl: new FormControl(null),
        destinationControl: new FormControl('', [
          Validators.required,
          locationValidator(),
        ]),
        packagingTypeControl: new FormControl<string>(null),
        paymentTermControl: new FormControl(null),
        additionalNoteControl: new FormControl(null),
        governingRule: new FormControl(null, Validators.required),
        weightGradeToGovernControl: new FormControl(null, Validators.required),
        marketType: new FormControl(this.marketType(), Validators.required),
      },
      { validators: [priceValidator()] }
    );

    this.recentlySearchedLocations.set(
      this.userService.getRecentlySearchedLocations()
    );
    this.store
      .select(selectUser)
      .pipe(
        map((user) => {
          if (user) {
            this.userId = user._id;
          } else {
            this.router.navigate(['../']);
            return;
          }
          if (user.default_origin) {
            this.newLotForm
              .get('originControl')
              .patchValue(user.default_origin, { emitValue: false });
          }
          if (user.default_commodity) {
            this.newLotForm
              .get('cropTypeControl')
              .setValue(user.default_commodity);
            this.defaultCommodity.set(user.default_commodity);
            this.favouriteCommodities.set([...user.favorable_commodities]);
          }
          // console.log('userDetails', res);
          return user;
        })
      )
      .subscribe();

    this.isLoading = true;
    const response$ = this.newLotOptionsService.watch({
      market: this.marketType(),
    }).valueChanges;
    // hold data from query
    response$.subscribe({
      next: (res) => {
        this.isLoading = false;
        this.units$ = of(res.data.units);
      },
      error: (err) => {
        console.error('Error loading lot options:', err.message);
        // navigate to previous page
        this.router.navigate(['../']);
      },
    });
    // wtf is this? Put this in the subscribe clause of the response$ observable
    this.origins$ = response$.pipe(
      map((res) => {
        // this.newLotForm.get('originControl').setValue(res.data.origins[0].name);
        return res.data.origins;
      })
    );
    this.origins$.subscribe();

    this.units$ = response$.pipe(map((res) => res.data.units));

    this.units$.subscribe({
      next: (units) => {
        this.newLotForm.get('priceUnitsControl').setValue(units[0]?.name_short);
      },
    });

    this.cropsGradesConditions$ = response$.pipe(
      map((result) => result.data.commodities)
    );
    this.timeFrames$ =
      this.marketType() === MarketType.Farm
        ? of([
            { name: Timeframe.Day },
            { name: Timeframe.Week },
            { name: Timeframe.GTC },
          ])
        : of([{ name: Timeframe.Day }]);

    this.deliveryTypes$ = response$.pipe(
      map((result) => {
        return result.data.deliveryTypes;
      })
    );

    this.newLotForm.get('basisPriceControl').patchValue({
      month: this.basisMonths[0].value,
      year: this.basisYears[0],
    });

    this.newLotForm
      .get('basisPriceControl')
      .valueChanges.pipe(
        map((res) => {
          this.basisQuote.set(
            `${res.basisPoints}${res.product_code}${res.month}${res.year}`
          );
          this.newLotForm.updateValueAndValidity();
        })
      )
      .subscribe();

    this.newLotForm
      .get('priceTypeControl')
      .valueChanges.pipe(
        map((res: PriceType) => {
          switch (res) {
            case PriceType.Cash:
              this.newLotForm
                .get('basisPriceControl')
                .patchValue(null, { emitValue: false });
              break;
            case PriceType.Basis:
              this.newLotForm.get('priceControl').patchValue(0);
              break;
          }
        })
      )
      .subscribe();

    this.newLotForm
      .get('destinationControl')
      .valueChanges.pipe(
        map((val) => {
          if (val !== '' && val.length > 2) {
            // console.log('val', val);
            this.searchingLocation.set(true);
          }
          return val;
        }),
        debounceTime(600),
        distinctUntilChanged(),
        filter((val) => val !== '' && val.length > 2),
        switchMap((res) => {
          this.searchingLocation.set(true);
          return this.searchDestinationsService
            .watch({ input: res })
            .valueChanges.pipe((options) => {
              // console.log('seach options', options);
              return options;
            });
        })
      )
      .subscribe({
        next: (rez) => {
          // console.log('search result of destination', rez);
          // this.newLotForm
          //   .get('destinationControl')
          //   .patchValue(rez.data.display_name, { emitValue: false });
          const arr =
            rez.data.searchLocations.map((d) => ({ name: d.display_name })) ||
            [];
          this.destinations$ = of(arr);
          this.searchingLocation.set(false);
        },
      });

    // extracting crop types, product types, grades, conditions and delivery types

    this.setupGradesConditionsParser();

    this.setupDeliveryTypesParser();
    // TODO implement price type : basis_product_code

    // Setting default values for Form
    // setting default origins
    this.origins$.pipe(take(1)).subscribe((origins) => {
      // console.log('piping origins', origins);
      // This selects first entry of array if user doesn't have default origin
      if (origins && !this.newLotForm.get('originControl').value) {
        if (origins.length === 1) {
          this.newLotForm.get('originControl').patchValue(origins[0].name);
        } else {
          origins.forEach((og) => {
            if (og.name === 'Canada') {
              this.newLotForm.get('originControl').patchValue(origins[0].name);
            }
          });
        }
      }
    });

    //setting default time frame
    this.timeFrames$.pipe(take(1)).subscribe({
      next: (timeFrames) => {
        if (timeFrames && timeFrames.length > 0) {
          this.newLotForm
            .get('timeFrameControl')
            .patchValue(timeFrames[0].name);
        }
      },
    });
    //setting default crop Years
    // this.cropYears$.pipe(take(1)).subscribe((cropYears) => {
    //   if (cropYears && cropYears.length > 0) {
    //     this.newLotForm.get('cropYearControl').setValue(cropYears[0].name);
    //   }
    // });

    // setting default deliveryType value
    this.deliveryTypes$.pipe(take(1)).subscribe((deliveryTypes) => {
      if (deliveryTypes && deliveryTypes.length) {
        const delTypes = [...deliveryTypes];
        delTypes.sort((a, b) => `${a.name}`.localeCompare(`${b.name}`));
        switch (this.marketType()) {
          case MarketType.Farm:
            this.newLotForm.get('deliveryTypeControl').setValue('FOB Farm');
            break;
          case MarketType.Feed:
            this.newLotForm.get('deliveryTypeControl').setValue('Delivered');
            break;
          case MarketType.Track:
            this.newLotForm
              .get('deliveryTypeControl')
              .setValue(delTypes[1].name);
            break;
          case MarketType.Export:
            this.newLotForm.get('deliveryTypeControl').setValue('CIF');
            break;
          default:
            this.newLotForm
              .get('deliveryTypeControl')
              .setValue(delTypes[0].name);
        }
      }
    });

    // defining currency prefix
    // this.newLotForm
    //   .get('currencyControl')
    //   .valueChanges.subscribe((currencyName) => {
    //     this.updateCurrencyPrefix(currencyName);
    //   });
    // // Initialize currency prefix based on the initial form value
    // const initialCurrency = this.newLotForm.get('currencyControl').value;
    // this.updateCurrencyPrefix(initialCurrency);

    //Set up minimum date for data range widget
    this.minDate = new Date();
    this.minDate.setHours(0, 0, 0, 0); // Reset time to midnight for accurate comparison
  }

  // change handlers for form field changes
  setupGradesConditionsParser() {
    this.newLotForm
      .get('originControl')
      .valueChanges.pipe(
        startWith(this.newLotForm.get('originControl').value),
        switchMap((origin) => this.getCropTypesofOrigin(origin))
      )
      .subscribe({
        next: (res) => {
          // set cropTypes
          this.commodities = res.commodities;
          this.commodities.sort((a, b) => a.name.localeCompare(b.name));
          // filter user's favourite commodities if commodities from origin includes them
          this.favouriteCommodities.update((val) =>
            val.filter((fav) =>
              this.commodities.some((item) => item.name === fav)
            )
          );
          const hasFav = this.commodities.some(
            (e) => e.name === this.defaultCommodity()
          );
          //set the origin form value here from the first entry of the new list
          if (
            this.newLotForm.get('cropTypeControl').value !== null &&
            this.newLotForm.get('cropTypeControl').value !== '' &&
            hasFav &&
            !history.state.lotData
          ) {
            this.newLotForm
              .get('cropTypeControl')
              .setValue(this.newLotForm.get('cropTypeControl').value, {
                emitValue: false,
              });
          } else {
            this.newLotForm
              .get('cropTypeControl')
              .setValue(this.commodities[0].name);
          }
        },
      });

    //getting product types
    this.newLotForm
      .get('cropTypeControl')
      .valueChanges.pipe(
        startWith(this.newLotForm.get('cropTypeControl').value),
        switchMap((cropType) => {
          logEvent(this.analytics, LogEventsEnum.commodity_selection_view);
          return this.getProductTypes(
            cropType,
            this.newLotForm.get('originControl').value
          );
        })
      )
      .subscribe(({ productTypes, priceTypes }) => {
        this.priceTypes$ = of(priceTypes);
        this.newLotForm
          .get('priceTypeControl')
          .patchValue(priceTypes[0], { emitValue: false });

        this.productTypes$ = of(productTypes); // Set productTypes

        // Reset Product Type selection
        this.productTypes$.pipe(take(1)).subscribe((productTypes) => {
          this.newLotForm
            .get('productTypeControl')
            .setValue(productTypes[0]?.name);
          // Check if there's only one item with a null name
          if (productTypes) {
            if (productTypes.length && productTypes[0].name === null) {
              this.showProductTypeControl = false;
            } else {
              this.showProductTypeControl = true;
            }
          }
          // Check if there's only one item with a null name
        });
      });

    //getting grades
    this.newLotForm
      .get('productTypeControl')
      .valueChanges.pipe(
        startWith(this.newLotForm.get('productTypeControl').value),
        switchMap((productType) => {
          return this.getGrades(
            this.newLotForm.get('cropTypeControl').value,
            productType,
            this.newLotForm.get('originControl').value
          );
        })
      )
      .subscribe(({ grades }) => {
        this.grades$ = of(grades); // Set grades

        // Reset Grades selection
        this.grades$.pipe(take(1)).subscribe((grades) => {
          this.newLotForm.get('gradeControl').setValue(grades[0]?.name);
          // Check if there's only one item with a null name
          if (grades.length === 1 && grades[0].name === null) {
            this.showGradeControl = false;
          } else {
            this.showGradeControl = true;
          }
        });
      });

    //getting specifications
    this.newLotForm
      .get('gradeControl')
      .valueChanges.pipe(
        startWith(this.newLotForm.get('gradeControl').value),
        switchMap((grade) => {
          return this.getSpecifications(
            this.newLotForm.get('cropTypeControl').value,
            this.newLotForm.get('productTypeControl').value,
            grade
          );
        })
      )
      .subscribe((specs) => {
        // Set specifications
        this.specifications$ = of(specs);
        this.specifications$.pipe(take(1)).subscribe((specs) => {
          if (specs.length && !this.repostingLot()) {
            (this.newLotForm.get('specificationsControl') as FormArray).clear();
            specs.forEach((spec) => {
              (this.newLotForm.get('specificationsControl') as FormArray).push(
                new FormGroup({
                  title: new FormControl(spec.title),
                  value: new FormControl(
                    spec.defaultValue,
                    spec.isRequired ? Validators.required : null
                  ),
                })
              );
            });
            this.newLotForm.get('specificationsControl').markAsDirty();
          }
          if (history.state.lotData) {
            this.repostingLot.set(true);
            this.marketType.set(MarketType[history.state.lotData.market_type]);
            this.newLotForm.patchValue(
              {
                originControl: history.state.lotData.origin,
                cropTypeControl: history.state.lotData.commodity,
                productTypeControl: history.state.lotData.product_type,
                gradeControl: history.state.lotData.grade,
                timeFrameControl: history.state.lotData.time_frame,
                quantityControl: history.state.lotData.volume_value,
                documentations: history.state.lotData.documentations,
                toleranceControl: history.state.lotData.tolerance,
                currencyControl: history.state.lotData.currency,
                priceTypeControl: this.lotsService.isCashPrice(
                  history.state.lotData.price
                )
                  ? PriceType.Cash
                  : PriceType.Basis,
                priceControl: history.state.lotData.price.amount,
                priceUnitsControl: history.state.lotData.price_units,
                deliveryWindowControl: {
                  start: history.state.lotData.ship_begin,
                  end: history.state.lotData.ship_end,
                },
                lot_type: this.lotType,
                deliveryTypeControl: history.state.lotData.delivery_type,
                deliveryOptionControl: history.state.lotData.delivery_type,
                containerTypeControl: history.state.lotData.container_type,
                destinationControl: history.state.lotData.destination,
                packagingTypeControl: history.state.lotData.packaging,
                paymentTermControl: history.state.lotData.payment_term,
                additionalNoteControl: history.state.lotData.additional_info,
                governingRule: history.state.lotData.rules_to_govern,
                weightGradeToGovernControl:
                  history.state.lotData.weight_grade_to_govern,
                marketType: MarketType[history.state.lotData.market_type],
              },
              { emitEvent: false, onlySelf: true }
            );
            if (history.state.lotData.specifications.length) {
              (this.newLotForm.get('specificationsControl') as FormArray).clear(
                {
                  emitEvent: false,
                }
              );
              history.state.lotData.specifications.forEach((spec) => {
                (
                  this.newLotForm.get('specificationsControl') as FormArray
                ).push(
                  new FormGroup({
                    title: new FormControl(spec.title),
                    value: new FormControl(spec.value),
                  }),
                  { emitEvent: false }
                );
              });
              this.newLotForm
                .get('specificationsControl')
                .markAsDirty({ onlySelf: true });
              history.replaceState({ lotData: null }, '');
            }
          } else {
            this.repostingLot.set(false);
          }
        });
      });

    // set deliveryTypes. Now tied to market type (not to grade or crop type)
    this.newLotForm
      .get('deliveryTypeControl')
      .valueChanges.pipe(
        startWith(this.newLotForm.get('deliveryTypeControl').value),
        distinctUntilChanged(),
        switchMap((deliveryType) => {
          return combineLatest({
            deliveryTypes: this.getDeliveryTypes(),
            deliveryTypeFormValue: of(deliveryType),
          });
        }),
        mergeMap(({ deliveryTypes, deliveryTypeFormValue }) => {
          this.deliveryTypeOptions$ = of(deliveryTypes.deliveryTypeOptions);
          return this.getDocumentation(deliveryTypeFormValue);
        })
      )
      .subscribe({
        next: (documentations) => {
          (this.newLotForm.get('documentationsControl') as FormArray).clear();
          if (documentations !== null) {
            this.currentDocumentations.set(documentations);
            documentations.forEach((doc) => {
              (this.newLotForm.get('documentationsControl') as FormArray).push(
                new FormGroup({
                  title: new FormControl<string>(doc.title),
                  value: new FormControl<string>(doc.defaultValue),
                })
              );
            });
          } else {
            this.currentDocumentations.set(null);
          }
        },
      });
  }

  private getCropTypesofOrigin(
    origin: string
  ): Observable<{ commodities: { name: string }[] }> {
    return this.cropsGradesConditions$.pipe(
      map((cropsGradesConditions) => {
        // this only return the first instace of the match.
        // need to return an array of all crops for origin
        const cropsArr = cropsGradesConditions.map((cgc) => {
          if (cgc.origin === origin) {
            return { name: cgc.commodity };
          }
          return;
        });
        cropsArr.sort((a, b) => a.name.localeCompare(b.name));
        this.commodities = cropsArr.filter((c) => c !== undefined);

        if (!cropsArr.length) {
          return { commodities: [] };
        }
        const commodities = cropsArr.filter((c) => c !== undefined);
        return { commodities };
      })
    );
  }

  private getProductTypes(
    cropType: string,
    origin: string
  ): Observable<{ productTypes: { name: string }[]; priceTypes: PriceType[] }> {
    return this.cropsGradesConditions$.pipe(
      map((cropsGradesConditions: CropsGradesCondition[]) => {
        const crop = cropsGradesConditions.find(
          (cgc) => cgc.commodity === cropType && cgc.origin === origin
        );

        if (!crop) {
          return { productTypes: [], priceTypes: [] };
        }

        let productTypes =
          crop.product_types?.map((pt) => ({ name: pt.product_type })) || [];

        this.newLotForm.get('priceTypeControl').setValue(crop.price_types[0]);

        this.newLotForm
          .get('basisPriceControl')
          .patchValue({ product_code: crop.basis_product_code });
        return { productTypes, priceTypes: crop.price_types };
      })
    );
  }

  private getGrades(
    cropType: string,
    productType: string,
    origin: string
  ): Observable<{
    grades: { name: string }[];
  }> {
    return this.cropsGradesConditions$.pipe(
      map((cropsGradesConditions) => {
        const crop = cropsGradesConditions.find(
          (cgc) => cgc.commodity === cropType && cgc.origin === origin
        );
        const product = crop?.product_types.find(
          (pt) => pt.product_type === productType
        );

        if (!crop || !product) {
          return { grades: [] };
        }

        let grades =
          product.grades_conditions?.map((gc) => ({ name: gc.grade })) || [];

        return { grades };
      })
    );
  }

  private getSpecifications(
    cropType: string,
    productType: string,
    grade: string
  ): Observable<Specifications[]> {
    // get through grades array and get
    return this.cropsGradesConditions$.pipe(
      map((cropsGradesConditions) => {
        // find new specfications array by filtering thorugh cropsGradesConditions by crop, prodcutType in array
        const crop = cropsGradesConditions.find(
          (cgc) => cgc.commodity === cropType
        );

        const product = crop?.product_types.find(
          (pt) => pt.product_type === productType
        );

        const gradeConditions = product?.grades_conditions.find(
          (gc) => gc.grade === grade
        );
        // console.log('gradeConditions obj', gradeConditions);
        const specifications = gradeConditions?.specifications;

        if (!specifications) {
          return [];
        }

        return specifications;
      })
    );
  }

  // TODO : update to not depend on cropsGradesConditions. It no longer depends on grade or commodities
  // replace this.cropsGradesConditions$ with deliverTypes$ as it is its own query
  private getDeliveryTypes(): Observable<{
    deliveryTypeOptions: { name: string }[];
  }> {
    return this.deliveryTypes$.pipe(
      map((deliveryTypes) => {
        const deliveryTypeOptions =
          deliveryTypes.map((dt) => ({ name: dt.name })) || [];
        if (!deliveryTypeOptions) {
          return { deliveryTypeOptions: [] };
        }
        // console.log('DELIVERY TYPES OPTIONS LIST', deliveryTypeOptions);
        deliveryTypeOptions.sort((a, b) =>
          `${a.name}`.localeCompare(`${b.name}`)
        );

        return { deliveryTypeOptions };

        // don't need any of this
        // const crop = cropsGradesConditions.find(
        //   (cgc) => cgc.commodity === cropType
        // );
        // const product = crop?.product_types.find(
        //   (pt) => pt.product_type === productType
        // );
        // const grade = product?.grades_conditions.find(
        //   (gc) => gc.grade === gradeType
        // );

        // if (!crop || !product || !grade) {
        //   return { deliveryTypeOptions: [] };
        // }

        // let deliveryTypeOptions =
        //   grade.grade_delivery_types?.map((dt) => ({ name: dt })) || [];

        // if (!deliveryTypeOptions.length) {
        //   deliveryTypeOptions =
        //     crop.delivery_types?.map((dt) => ({ name: dt })) || [];
        // }

        // return { deliveryTypeOptions };
      })
    );
  }

  getDocumentation(
    deliveryType: string | null
  ): Observable<Documentation[] | null> {
    if (deliveryType === null) {
      return of(null);
    }
    return this.deliveryTypes$.pipe(
      map((deliveryTypes: DeliveryType[]) => {
        const doc =
          deliveryTypes.find((del) => del.name === deliveryType) || null;
        return doc.documentations;
      })
    );
  }

  setupDeliveryTypesParser() {
    //getting destinations
    this.newLotForm
      .get('deliveryTypeControl')
      .valueChanges.pipe(
        startWith(this.newLotForm.get('deliveryTypeControl').value),
        switchMap((deliveryType) => this.processDeliveryTypes(deliveryType))
      )
      .subscribe(
        ({
          paymentTerms,
          tolerances,
          deliveryOptions,
          containerTypes,
          rulesToGovern,
          weightGradeToGovern,
          currencies,
          packagingTypes,
          priceUnits,
          documentations,
        }) => {
          this.paymentTerms$ = of(paymentTerms);
          this.tolerances$ = of(tolerances);
          this.deliveryOptions$ = of(deliveryOptions);
          this.containerTypes$ = of(containerTypes);
          this.rulesToGovern$ = of(rulesToGovern);
          this.weightGradeToGovern$ = of(weightGradeToGovern);
          this.currencies$ = of(currencies);
          this.packagingTypes$ = of(packagingTypes);
          this.priceUnits$ = of(priceUnits);
          this.documentations$ = of(documentations);

          // Reset Payment terms selection
          this.paymentTerms$.pipe(take(1)).subscribe((paymentTerms) => {
            this.newLotForm
              .get('paymentTermControl')
              .setValue(paymentTerms[0]?.name);
          });
          // Reset Tolerence selection
          this.tolerances$.pipe(take(1)).subscribe((tolerances) => {
            this.newLotForm
              .get('toleranceControl')
              .setValue(tolerances[0]?.name);
          });
          // Reset Delivery Option selection
          this.deliveryOptions$.pipe(take(1)).subscribe((deliveryOptions) => {
            this.newLotForm
              .get('deliveryOptionControl')
              .setValue(deliveryOptions[0]?.name);
          });
          // Reset Container Type selection
          this.containerTypes$.pipe(take(1)).subscribe((containerTypes) => {
            this.newLotForm
              .get('containerTypeControl')
              .setValue(containerTypes[0]?.name);
          });
          // reset rulesToGovern selection
          this.rulesToGovern$.pipe(take(1)).subscribe((rulesToGovern) => {
            this.newLotForm
              .get('governingRule')
              .setValue(rulesToGovern[0]?.name);
          });
          //weightGradeToGovern selection
          this.weightGradeToGovern$
            .pipe(take(1))
            .subscribe((weightGradeToGovern) => {
              this.newLotForm
                .get('weightGradeToGovernControl')
                .setValue(weightGradeToGovern[0]?.name);
            });
          // reset currencies
          this.currencies$.pipe(take(1)).subscribe((currencies) => {
            this.newLotForm
              .get('currencyControl')
              .setValue(currencies[0]?.name);
          });
          // reset packaginTypes selection
          this.packagingTypes$.pipe(take(1)).subscribe((packagingTypes) => {
            this.newLotForm
              .get('packagingTypeControl')
              .setValue(packagingTypes[0]?.name);
          });
          // reset priceUnits form Control
          this.priceUnits$.pipe(take(1)).subscribe((priceUnit) => {
            this.newLotForm
              .get('priceUnitsControl')
              .setValue(priceUnit[0]?.name);
          });

          this.documentations$.pipe(take(1)).subscribe();
        }
      );
  }

  // returns all the fields into array of objects with name property so they can be used in autocomplete component
  private processDeliveryTypes(deliveryType: string): Observable<{
    paymentTerms: { name: string }[];
    tolerances: { name: string }[];
    deliveryOptions: { name: string }[];
    containerTypes: { name: string }[];
    rulesToGovern: { name: string }[];
    weightGradeToGovern: { name: string }[];
    currencies: { name: string }[];
    packagingTypes: { name: string }[];
    priceUnits: { name: string }[];
    documentations?: Documentation[] | null;
  }> {
    return this.deliveryTypes$.pipe(
      map((deliveryTypes) => {
        const delivery = deliveryTypes.find((dt) => dt.name === deliveryType);
        if (!delivery) {
          return {
            paymentTerms: [],
            tolerances: [],
            deliveryOptions: [],
            containerTypes: [],
            rulesToGovern: [],
            weightGradeToGovern: [],
            currencies: [],
            packagingTypes: [],
            priceUnits: [],
          };
        }

        let paymentTerms =
          delivery.payment_terms?.map((pt) => ({ name: pt })) || [];
        let tolerances = delivery.tolerances?.map((t) => ({ name: t })) || [];
        let deliveryOptions =
          delivery.delivery_options?.map((d) => ({ name: d })) || [];
        let containerTypes =
          delivery.container_types?.map((ct) => ({ name: ct })) || [];
        let rulesToGovern =
          delivery.rules_to_govern?.map((rtg) => ({ name: rtg })) || [];
        let weightGradeToGovern =
          delivery.weight_grade_to_govern?.map((wtg) => ({ name: wtg })) || [];
        let currencies =
          delivery.currencies?.map((crny) => ({ name: crny })) || [];
        let packagingTypes =
          delivery.packaging_types?.map((pkg) => ({ name: pkg })) || [];
        let priceUnits =
          delivery.price_units?.map((pu) => ({ name: pu })) || [];
        let docs: Documentation[] = delivery.documentations || null;
        return {
          paymentTerms,
          tolerances,
          deliveryOptions,
          containerTypes,
          rulesToGovern,
          weightGradeToGovern,
          currencies,
          packagingTypes,
          priceUnits,
          documentations: docs,
        };
      })
    );
  }

  onSubmit() {
    const formValues = this.newLotForm.value;
    if (this.newLotForm.invalid) {
      Object.keys(this.newLotForm.controls).forEach((field) => {
        const control = this.newLotForm.get(field);
        if (control.invalid) {
          // console.log(`Invalid field: ${field}`);
          // maybe add snackbar to show invalid fields?
        }
      });
      this._snackBar.open('Invalid Field(s)', 'Close', {
        duration: 5000,
        panelClass: ['blue-snackbar'],
      });
      this.newLotForm.markAllAsTouched(); // This will trigger the error messages
      return;
    }
    //open summary dialog
    const dialogRef = this.dialog.open(NewLotSummaryDialogComponent, {
      data: {
        formValues: {
          ...formValues,
          priceControl:
            formValues.priceTypeControl === 'Cash'
              ? { amount: formValues.priceControl }
              : formValues.basisPriceControl,
          documentationsControl:
            formValues.documentationsControl.length > 0
              ? formValues.documentationsControl
              : null,
        },
        lotType: this.lotType,
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirm) {
        logEvent(this.analytics, LogEventsEnum.lots_summary_submit_tap);
        const inputs = {
          origin: formValues.originControl,
          lot_type: this.lotType,
          commodity: formValues.cropTypeControl,
          product_type: formValues.productTypeControl,
          grade: formValues.gradeControl,
          basis_price: formValues.basisPriceControl,
          indication: formValues.indicationControl,
          price_type: formValues.priceTypeControl,
          // price_type: PriceType.Cash,
          cash_price: { amount: formValues.priceControl },
          currency: formValues.currencyControl,
          volume_value: formValues.quantityControl,
          price_units: formValues.priceUnitsControl,
          units: 'MT',
          ship_begin: formValues.deliveryWindowControl?.start,
          ship_end: formValues.deliveryWindowControl?.end,
          // destination: formValues.destinationTypeControl,
          destination: formValues.destinationControl,
          delivery_type: formValues.deliveryTypeControl,
          time_frame: formValues.timeFrameControl,
          additional_info:
            formValues.additionalNoteControl == null
              ? ''
              : formValues.additionalNoteControl,
          payment_term: formValues.paymentTermControl,
          specifications: formValues.specificationsControl,
          documentations:
            formValues.documentationsControl.length > 0
              ? formValues.documentationsControl
              : null,
          container_type: formValues.containerTypeControl,
          rules_to_govern: formValues.governingRule, // Replace null with the correct form control if available
          weight_grade_to_govern: formValues.weightGradeToGovernControl,
          delivery_option: formValues.deliveryOptionControl,
          tolerance: formValues.toleranceControl,
          packaging: formValues.packagingTypeControl,
          market_type: this.marketType(),
        };
        if (this.newLotForm.get('priceTypeControl').value === PriceType.Basis) {
          delete inputs.cash_price;
        } else {
          delete inputs.basis_price;
        }
        if (inputs.grade === null || inputs.grade === undefined) {
          inputs.grade = '-';
        }

        const filteredData = Object.fromEntries(
          Object.entries(inputs).filter(([_, value]) => value !== null)
        );

        filteredData.specifications = filteredData.specifications.filter(
          (spec) => spec.value !== null
        );

        this.createLotService
          .mutate({
            input: filteredData,
          })
          .subscribe({
            next: (response) => {
              const lotResponse = response.data?.createLot; // Correctly access the nested data
              if (lotResponse && lotResponse._id) {
                // add searched location to recently searched
                this.userService.addRecentlySearchedLocation(
                  lotResponse.destination
                );
                this.router.navigate(['app/lots']);
              }
            },
            error: (error) => {
              // Log only the error message — never the form values (trade-sensitive).
              console.error('Error creating lot:', error.message);
              this._snackBar.open(
                `Error creating ${this.lotType}: ${error.message
                  .toString()
                  .substring(0, 35)}...`,
                'close',
                {
                  duration: 5000,
                  panelClass: 'blue-snackbar',
                }
              );
            },
          });
      }
    });
  }

  onBidsAndOffersScroll(event: any) {
    const currentScroll = event.target.scrollTop;
    const maxScroll = event.target.scrollHeight - event.target.clientHeight;

    if (currentScroll >= maxScroll) {
      this.buttonOpacity = 1; // Scrolled to the bottom, set button opacity to 1
    } else {
      // Check if scrolling down or up
      if (this.lastScrollTop < maxScroll) {
        if (currentScroll > this.lastScrollTop && this.lastScrollTop >= 0) {
          // Scrolling down
          this.buttonOpacity = 0.5;
        } else if (currentScroll < this.lastScrollTop) {
          // Scrolling up
          this.buttonOpacity = 1;
        }
      }
    }

    // Update lastScrollTop for the next scroll event
    this.lastScrollTop = currentScroll;
  }

  Cancel() {
    this.router.navigate(['app/lots']);
  }

  ngOnDestroy(): void {
    this.documentations$ = of(null);
    this.repostingLot.set(false);
  }
}
